/**
 * 🎛 Phase 12.1 — Promotion Control Center
 *
 * Single admin card. Sliders + numeric inputs for every knob in
 * `platform_config`. Grouped by section. Inline "Save", "Reset to
 * default", and "History" per key. No save-everything button —
 * every change is atomic and audited.
 */
import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import PromoOperationsPanel from "@/components/admin/PromoOperationsPanel";
import PromoHealthPanel from "@/components/admin/PromoHealthPanel";
import BuildVersionStamp from "@/components/admin/BuildVersionStamp";

const SECTION_META = {
  promo:    { icon: "🔥", title: "Promo Plays",   blurb: "Capped grants applied when a track is featured. Phase 15.0: promo.mode controls FIXED (use exact .amount) vs DYNAMIC (random in range_min/range_max). Per-tier lifetime caps replace the single global cap — global cap is a safety net only." },
  boost:    { icon: "🚀", title: "Reach Boosts",  blurb: "Random reach target ranges + initial burst fraction applied when a feature/spotlight activates." },
  momentum: { icon: "📈", title: "Momentum Chips", blurb: "Thresholds that switch a track's public momentum label." },
  economy:  { icon: "💎", title: "Economy",        blurb: "Creator-side currency conversion. Knobs are configurable but inactive until you set a value." },
  contact:  { icon: "✉️", title: "Contact & Brand", blurb: "Editable public support email, social URLs, FAQ block, and Support page tagline. No redeploy needed." },
};

const PromotionControlCenter = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [historyKey, setHistoryKey] = useState(null);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/admin/config");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load configuration");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveKey = async (key, raw, type) => {
    setBusyKey(key);
    try {
      let value = raw;
      if (type === "int") value = parseInt(raw, 10);
      else if (type === "float") value = parseFloat(raw);
      else if (type === "bool") value = raw === true || raw === "true";
      else if (type === "null" && raw === "") value = null;
      await api.patch(`/admin/config/${encodeURIComponent(key)}`, { value });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusyKey(null);
    }
  };

  const resetKey = async (key) => {
    if (!window.confirm(`Reset ${key} to its default?`)) return;
    setBusyKey(key);
    try {
      await api.post(`/admin/config/${encodeURIComponent(key)}/reset`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusyKey(null);
    }
  };

  const openHistory = async (key) => {
    setHistoryKey(key); setHistory([]);
    try {
      const r = await api.get(`/admin/config/${encodeURIComponent(key)}/history?limit=20`);
      setHistory(r.data.history || []);
    } catch {
      setHistory([]);
    }
  };

  return (
    <Card
      className="p-6"
      style={{ background: "rgba(34, 24, 50, 0.35)", borderColor: "rgba(168, 85, 247, 0.25)" }}
      data-testid="promotion-control-center"
    >
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <span>🎛</span> Promotion Control Center
          </h3>
          <p className="text-sm text-gray-400 mt-1 max-w-2xl">
            Live tunables for promotion, reach boosts, momentum chips, and the creator economy.
            Every change is audited. Defaults match historical hardcoded values, so newly-seeded
            keys preserve current behaviour until you adjust them.
          </p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          className="text-white border-gray-700"
          data-testid="promotion-config-refresh"
        >
          {loading ? "Loading…" : "Refresh"}
        </Button>
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-200 text-sm mb-3">
          {error}
        </div>
      )}

      {/* Phase 14.0.1 — build/version stamp for deploy parity audits */}
      <BuildVersionStamp />

      {/* Phase 15.0 — 🩺 Promo Health snapshot (mode + tier caps + scheduler counts) */}
      <PromoHealthPanel />

      {/* Phase 13.2 — admin promo operations (backfill + live verify) */}
      <PromoOperationsPanel />

      {data && Object.entries(data.sections || {}).map(([section, rows]) => {
        const meta = SECTION_META[section] || { icon: "⚙", title: section, blurb: "" };
        return (
          <div key={section} className="mb-6" data-testid={`config-section-${section}`}>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-lg">{meta.icon}</span>
              <h4 className="text-white font-bold">{meta.title}</h4>
            </div>
            {meta.blurb && <p className="text-xs text-gray-500 mb-3 max-w-3xl">{meta.blurb}</p>}
            <div className="rounded-xl border border-purple-500/15 bg-black/30 divide-y divide-purple-500/10">
              {rows.map((row) => (
                <ConfigRow
                  key={row.key}
                  row={row}
                  busy={busyKey === row.key}
                  onSave={(v) => saveKey(row.key, v, row.value_type)}
                  onReset={() => resetKey(row.key)}
                  onHistory={() => openHistory(row.key)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {historyKey && (
        <HistoryDrawer
          configKey={historyKey}
          history={history}
          onClose={() => setHistoryKey(null)}
        />
      )}
    </Card>
  );
};

const ConfigRow = ({ row, busy, onSave, onReset, onHistory }) => {
  const [raw, setRaw] = useState(row.value ?? "");
  useEffect(() => { setRaw(row.value ?? ""); }, [row.value]);
  const dirty = String(raw) !== String(row.value ?? "");
  const inactive = row.value_type === "null";

  return (
    <div className="p-3 sm:p-4" data-testid={`config-row-${row.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <code className="text-amber-200 text-[12px] font-mono break-all">{row.key}</code>
          <p className="text-xs text-gray-400 mt-1">{row.description}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {row.is_dirty && (
            <span className="text-[9px] uppercase tracking-wider font-black text-pink-300 bg-pink-500/15 border border-pink-500/40 px-2 py-0.5 rounded-full">
              modified
            </span>
          )}
          {inactive && (
            <span className="text-[9px] uppercase tracking-wider font-black text-amber-200 bg-amber-500/15 border border-amber-500/40 px-2 py-0.5 rounded-full">
              inactive
            </span>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input
          type={inactive ? "text" : row.value_type === "bool" ? "text" : "number"}
          value={raw === null || raw === undefined ? "" : raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={inactive ? "(empty = inactive)" : `default ${row.default_value}`}
          className="w-44 bg-gray-900 text-white border-gray-700"
          data-testid={`config-input-${row.key}`}
          disabled={busy}
          step={row.value_type === "float" ? "0.01" : "1"}
        />
        <span className="text-xs text-gray-500">default <code className="text-gray-300">{String(row.default_value)}</code></span>
        <Button
          onClick={() => onSave(raw)}
          disabled={!dirty || busy}
          className={`text-xs ${dirty ? "bg-amber-500 text-black hover:bg-amber-400" : "bg-gray-700 text-gray-400"}`}
          data-testid={`config-save-${row.key}`}
        >
          {busy ? "…" : "Save"}
        </Button>
        <Button
          onClick={onReset}
          disabled={busy || !row.is_dirty}
          variant="outline"
          className="text-xs border-gray-700 text-white"
          data-testid={`config-reset-${row.key}`}
        >
          Reset
        </Button>
        <Button
          onClick={onHistory}
          disabled={busy}
          variant="ghost"
          className="text-xs text-purple-300 hover:text-purple-200"
          data-testid={`config-history-${row.key}`}
        >
          History
        </Button>
      </div>
    </div>
  );
};

const HistoryDrawer = ({ configKey, history, onClose }) => (
  <div
    className="fixed inset-0 z-[100] bg-black/70 flex items-end sm:items-center justify-center p-2 sm:p-6"
    onClick={onClose}
    data-testid="config-history-drawer"
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="w-full max-w-lg max-h-[80vh] overflow-auto bg-gray-950 border border-purple-500/30 rounded-2xl p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-white font-bold">History</h4>
          <code className="text-[11px] text-amber-200">{configKey}</code>
        </div>
        <Button onClick={onClose} variant="ghost" className="text-gray-400">Close</Button>
      </div>
      {history.length === 0 ? (
        <p className="text-gray-500 text-sm py-6 text-center">No changes recorded yet.</p>
      ) : (
        <ul className="space-y-2">
          {history.map((h, i) => (
            <li key={i} className="text-xs text-gray-300 border border-purple-500/15 rounded-lg p-3 bg-black/40">
              <div className="flex justify-between items-baseline gap-2">
                <code className="text-amber-200">{JSON.stringify(h.value)}</code>
                <span className="text-gray-500 text-[10px]">{h.at}</span>
              </div>
              {h.by && <p className="text-[10px] text-gray-500 mt-1">by {h.by}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

export default PromotionControlCenter;
